// DialogLayout — Blue theme, 50/50 split
//
// Left panel (50%): ConceptMapHeader + ConceptMap (React Flow canvas)
// Right panel (50%): ChatPanel
// Mobile: ConceptMap stacks above ChatPanel
// Content blocks appear as a floating overlay over the concept map canvas.

import type { ReactNode } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type { BloomLevel, TeachingForm } from '../../types';
import ConceptMap from '../visualizations/ConceptMap';
import ConceptMapHeader from '../visualizations/ConceptMapHeader';
import ContentBlock from '../visualizations/ContentBlock';

interface DialogLayoutProps {
  chatPanel: ReactNode;
  conceptMapNodes: Node[];
  conceptMapEdges: Edge[];
  conceptMapLoading: boolean;
  onNodeDragStop: (nodeId: string, position: { x: number; y: number }) => void;
  currentBloomLevel: BloomLevel;
  totalSessions?: number;
  lastTeachingForm?: TeachingForm;
  /** When true, disables node drag interactions (exam mode) */
  readOnly?: boolean;
  /** Called when a concept map node is clicked in dialog (Samtale) mode */
  onNodeClick?: (nodeId: string, nodeLabel: string) => void;
  /** Content block to display as a floating overlay over the concept map */
  activeContentBlock?: {
    title: string;
    content: string;
    type: 'definition' | 'example' | 'analogy' | 'summary';
    nodeRef?: string;
    depth?: 'full' | 'condensed' | 'minimal';
  } | null;
  onDismissContentBlock?: () => void;
}

export default function DialogLayout({
  chatPanel,
  conceptMapNodes,
  conceptMapEdges,
  conceptMapLoading,
  onNodeDragStop,
  currentBloomLevel,
  totalSessions = 0,
  lastTeachingForm,
  readOnly = false,
  onNodeClick,
  activeContentBlock,
  onDismissContentBlock,
}: DialogLayoutProps) {
  return (
    <div className="flex flex-col md:flex-row h-full">
      <div className="relative flex flex-col h-[40vh] md:h-full md:w-[50%]">
        <ConceptMapHeader
          currentBloomLevel={currentBloomLevel}
          totalSessions={totalSessions}
          lastTeachingForm={lastTeachingForm}
        />
        <div className="flex-1 min-h-0">
          <ConceptMap
            nodes={conceptMapNodes}
            edges={conceptMapEdges}
            isLoading={conceptMapLoading}
            onNodeDragStop={onNodeDragStop}
            readOnly={readOnly}
            onNodeClick={onNodeClick}
          />
        </div>
        {activeContentBlock && (
          <div className="absolute bottom-4 left-4 right-4 z-10 max-w-md mx-auto
            bg-gray-900/95 backdrop-blur-sm rounded-lg border border-gray-700 shadow-xl
            p-4 animate-fade-in-simple">
            <ContentBlock
              title={activeContentBlock.title}
              content={activeContentBlock.content}
              type={activeContentBlock.type}
              accentColor="blue"
              depth={activeContentBlock.depth}
            />
            <button
              onClick={onDismissContentBlock}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-200 p-1"
              aria-label="Luk"
            >
              ✕
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 md:flex-none md:w-[50%] min-h-0">
        {chatPanel}
      </div>
    </div>
  );
}
